import type { useHomePresentation } from "./Hooks";
import React, { useState, useRef } from "react";
import JumbotronComponent from "../../components/Jumbotron/";
import CustombuttonComponent from "../../components/CustomButton";
import FeatureitemheaderComponent from "../../components/FeatureItemHeader";
import FeatureitemComponent from "../../components/FeatureItem";
import CheckUsOutComponent from "../../components/Checkusout";
import FooterComponent from "../../components/Footer";

export interface HomeComponentProps {
  homePageData: any;
  error: any;
  isLoading: boolean;
}

export function HomeContentPiece({
  homePageData,
  error,
  isLoading,
  presentation,
}: HomeComponentProps & {
  presentation: ReturnType<typeof useHomePresentation>;
}) {
  const {
    featureSetIndex,
    categoryIndex,
    phase,
    categories,
    activeCategory,
    drinkCategories,
    drinkCategoryIndex,
    drinkPhase,
    activeDrinkCategory,
    handleNavigate,
    handleDrinkNavigate,
  } = presentation;

  return (
    <div className="bg-sow-black min-h-screen">
      {isLoading && (
        <div className="flex items-center justify-center h-screen text-sow-white">
          <div className="animate-pulse text-center">
            <p className="text-lg font-semibold mb-2">Loading deliciousness…</p>
            <p className="text-sm opacity-75">Fetching home page data</p>
          </div>
        </div>
      )}
      {!isLoading && error && (
        <div className="flex flex-col items-center justify-center h-screen text-center px-6">
          <h1 className="text-2xl font-bold text-red-400 mb-4">
            Unable to load menu
          </h1>
          <p className="text-sow-white/80 max-w-md">
            We hit a snag loading the home page. Please refresh or try again
            later.
          </p>
        </div>
      )}
      {!isLoading && !error && homePageData && (
        <>
          <JumbotronComponent
            videoUrl={homePageData?.bannerVideoUrl}
            customButton={<CustombuttonComponent text={"Order Now"} />}
            imgUrl={homePageData?.sowLogo}
            imgAlt={homePageData?.logoAlt}
            navLinkUrl={homePageData?.orderNowUrl}
          />
          <section
            className="px-4 sm:px-6 md:px-8 lg:px-12 py-10 sm:py-14 relative"
            aria-labelledby="featured-intro-title"
          >
            <div className="max-w-5xl mx-auto text-center">
              <h2
                id="featured-intro-title"
                className="hammer relative inline-block text-transparent bg-clip-text bg-gradient-to-r from-sow-gold via-amber-300 to-sow-darkGold drop-shadow-[0_2px_6px_rgba(255,216,97,0.35)] tracking-wide leading-tight font-extrabold text-3xl sm:text-4xl md:text-5xl lg:text-6xl"
              >
                <span className="block">
                  {homePageData?.featuredHeading ||
                    "Our favorites, crafted to be yours."}
                </span>
                <span className="block text-base sm:text-lg md:text-xl lg:text-2xl mt-4 text-sow-white font-normal normal-case tracking-normal bg-none text-white drop-shadow-none">
                  {homePageData?.featuredDescription ||
                    "Check out what’s cooking below."}
                </span>
                <span
                  className="pointer-events-none absolute -bottom-4 left-1/2 h-[3px] w-56 -translate-x-1/2 bg-gradient-to-r from-transparent via-sow-gold to-transparent"
                  aria-hidden="true"
                />
              </h2>
            </div>
          </section>
          <div className="px-4 sm:px-6 md:px-8 lg:px-12 pt-6 sm:pt-8">
            <FeatureitemheaderComponent
              featureItems={categories}
              currentIndex={categoryIndex}
              onNavigate={handleNavigate}
            />
            <div
              className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 lg:gap-12 mt-6 sm:mt-8 ${
                phase === "exiting"
                  ? "feature-grid-exit"
                  : phase === "entering"
                  ? "feature-grid-enter"
                  : ""
              }`}
            >
              {activeCategory?.items?.map((item: any, index: number) => (
                <FeatureitemComponent
                  key={`${featureSetIndex}-${categoryIndex}-${index}-${phase}`}
                  imgUrl={item?.img}
                  title={item?.title}
                  description={item?.description}
                  price={item?.price}
                  link={item?.link}
                  isOnlineOrdering={item?.isOnlineOrdering}
                  delayMs={phase === "entering" ? index * 110 : 0}
                />
              ))}
            </div>

            {drinkCategories.length > 0 && (
              <div className="mt-20 sm:mt-28">
                <h3 className="hammer relative mx-auto flex flex-col items-center justify-center text-center text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-sow-gold to-amber-400 tracking-wide leading-tight font-extrabold text-3xl sm:text-4xl md:text-5xl">
                  <span className="block w-full">
                    {homePageData?.drinksHeading || "Raise a Glass."}
                  </span>
                  <span className="block w-full text-base sm:text-lg md:text-xl lg:text-2xl mt-3 text-sow-white font-normal normal-case tracking-normal bg-none text-white drop-shadow-none">
                    {homePageData?.drinksDescription ||
                      "Explore our featured drinks below."}
                  </span>
                  <span
                    className="pointer-events-none absolute -bottom-3 left-1/2 h-[3px] w-40 -translate-x-1/2 bg-gradient-to-r from-transparent via-amber-300 to-transparent"
                    aria-hidden="true"
                  />
                </h3>
                <div className="mt-20">
                  <FeatureitemheaderComponent
                    featureItems={drinkCategories}
                    currentIndex={drinkCategoryIndex}
                    onNavigate={handleDrinkNavigate}
                  />
                  <div
                    className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 lg:gap-12 mt-6 sm:mt-8 ${
                      drinkPhase === "exiting"
                        ? "feature-grid-exit"
                        : drinkPhase === "entering"
                        ? "feature-grid-enter"
                        : ""
                    }`}
                  >
                    {activeDrinkCategory?.items?.map(
                      (drink: any, index: number) => (
                        <FeatureitemComponent
                          key={`drink-${drinkCategoryIndex}-${index}-${drinkPhase}`}
                          imgUrl={drink?.img}
                          title={drink?.title}
                          description={drink?.description}
                          price={drink?.price}
                          link={drink?.link}
                          isOnlineOrdering={drink?.isOnlineOrdering}
                          delayMs={drinkPhase === "entering" ? index * 110 : 0}
                        />
                      )
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* New section here */}
          {!isLoading && !error && !homePageData && (
            <div className="flex flex-col items-center justify-center h-screen text-center px-6">
              <h1 className="text-2xl font-bold text-sow-white mb-4">
                Nothing to display
              </h1>
              <p className="text-sow-white/70 max-w-md">
                We couldn't load the home page content. It may be generating or
                unavailable. Please refresh shortly.
              </p>
            </div>
          )}
          <hr className="mt-16 sm:mt-20 lg:mt-24 mb-16 sm:mb-20 lg:mb-24" />
          {homePageData?.checkUsOut?.visible !== false && (
            <CheckUsOutComponent
              imgTopSrc={homePageData?.checkUsOut?.firstImg}
              imgTopAlt={
                homePageData?.checkUsOut?.firstAlt ||
                "Guest enjoying a cocktail"
              }
              imgMiddleSrc={homePageData?.checkUsOut?.thirdImg}
              imgMiddleAlt={
                homePageData?.checkUsOut?.thirdAlt ||
                "Friends dining on the patio"
              }
              imgBottomSrc={homePageData?.checkUsOut?.secondImg}
              imgBottomAlt={
                homePageData?.checkUsOut?.secondAlt ||
                "Friends dining on the patio"
              }
              title={homePageData?.checkUsOut?.title}
              copy={homePageData?.checkUsOut?.content}
              variant={"tiered"}
              showBubbles={true}
              bubbleAnimation={"drift"}
            />
          )}
          <FooterComponent
            imageUrl={homePageData?.sowLogo}
            backgroundUrl={homePageData?.bkgImg}
          />
        </>
      )}
    </div>
  );
}
